import React from 'react';
import { Clock } from 'lucide-react';

interface CurrentPriceCardProps {
  price: number | null;
  currentHour: number;
}

function getPriceZoneLabel(price: number): string {
  if (price < 0) return 'Negativpreis';
  if (price <= 80) return 'Normaler Bereich';
  if (price <= 150) return 'Hoher Preis';
  return 'Sehr hoher Preis';
}

function getPriceZoneClass(price: number | null): string {
  if (price === null) return 'text-muted-foreground';
  if (price < 0) return 'text-price-negative';
  if (price <= 80) return 'text-price-normal';
  if (price <= 150) return 'text-price-high';
  return 'text-price-very-high';
}

function getPriceBorderClass(price: number | null): string {
  if (price === null) return 'border-border';
  if (price < 0) return 'border-price-negative';
  if (price <= 80) return 'border-primary';
  if (price <= 150) return 'border-price-high';
  return 'border-price-very-high';
}

function getPriceBgClass(price: number | null): string {
  if (price === null) return '';
  if (price < 0) return 'bg-green-950/30';
  if (price <= 80) return 'bg-blue-950/30';
  if (price <= 150) return 'bg-amber-950/30';
  return 'bg-red-950/30';
}

export default function CurrentPriceCard({
  price,
  currentHour,
}: CurrentPriceCardProps) {
  const hourLabel = `${String(currentHour).padStart(2, '0')}:00 – ${String(currentHour + 1).padStart(2, '0')}:00 Uhr`;
  const priceZoneClass = getPriceZoneClass(price);
  const borderClass = getPriceBorderClass(price);
  const bgClass = getPriceBgClass(price);

  return (
    <div
      className={`h-full rounded-md border-2 ${borderClass} ${bgClass} p-5 sm:p-6 flex flex-col justify-between min-h-[140px]`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="label-caps">Aktueller Preis</p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock size={12} strokeWidth={2} />
          <span className="font-tabular">{hourLabel}</span>
        </div>
      </div>

      <div className="flex items-end gap-3">
        <span
          className={`font-tabular font-bold leading-none ${priceZoneClass}`}
          style={{ fontSize: '3rem' }}
        >
          {price !== null ? (price >= 0 ? price.toFixed(2) : price.toFixed(2)) : '—'}
        </span>
        <div className="flex flex-col mb-1">
          <span className="text-base font-semibold text-muted-foreground leading-tight">
            €/MWh
          </span>
          {price !== null && (
            <span className={`text-xs font-medium mt-0.5 ${priceZoneClass}`}>
              {getPriceZoneLabel(price)}
            </span>
          )}
        </div>
      </div>

      {/* Cents per kWh conversion */}
      {price !== null && (
        <p className="mt-3 text-xs text-muted-foreground font-tabular">
          ≈{' '}
          <span className="text-foreground font-medium">
            {(price / 10).toFixed(2)} ct/kWh
          </span>{' '}
          (Börse, ohne Netzentgelte & Steuern)
        </p>
      )}
    </div>
  );
}